'use client';

import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AddIcon, MinusIcon } from 'tdesign-icons-react';
import styles from '../../design-assets.module.css';
import {
  getDesignAssetDetail,
  type DesignAssetGroupPreview,
  type DesignAssetPreview,
} from '../../api';
import { buildDesignAssetPath } from '../../route-path';
import { AssetSection } from './asset-section';
import {
  DesignEid,
  DesignPageId,
  dtElementParams,
  useDesignDetailDtAttrs,
} from '../../tracking';

interface DetailPageProps {
  params: { group: string; asset: string };
}

function DirectoryMenuIcon() {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="20"
      viewBox="0 0 20 20"
      width="16"
    >
      <path
        d="M0 3h20v2H0V3zm0 6h20v2H0V9zm0 6h20v2H0v-2z"
        fill="currentColor"
      />
    </svg>
  );
}

export default function DesignAssetDetail({ params }: DetailPageProps) {
  const groupToken = params.group;
  const assetToken = params.asset;
  const detailKey = `${groupToken}:${assetToken}`;
  const [detailState, setDetailState] = useState<{
    key: string;
    asset?: DesignAssetPreview;
    groups: DesignAssetGroupPreview[];
  }>({ key: '', groups: [] });
  const currentDetail = detailState.key === detailKey ? detailState : undefined;
  const groups = currentDetail?.groups || [];
  const realGroupID = currentDetail?.asset?.group_id;
  const realAssetID = currentDetail?.asset?.asset_id;
  const group = groups.find((item) => item.group_id === realGroupID);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [activeAssetId, setActiveAssetId] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const detailPgid = DesignPageId.Detail;
  const pageDtAttrs = useDesignDetailDtAttrs(
    realGroupID,
    group?.group_name,
    realAssetID
  );
  const isReloadRef = useRef(
    typeof window !== 'undefined' &&
      performance.getEntriesByType('navigation')[0]?.toJSON().type === 'reload'
  );

  useEffect(() => {
    let isActive = true;
    getDesignAssetDetail(groupToken, assetToken)
      .then((data) => {
        if (!isActive) return;
        setDetailState({
          key: detailKey,
          asset: data.asset,
          groups: Array.isArray(data.groups) ? data.groups : [],
        });
      })
      .catch(() => {
        if (isActive) setDetailState({ key: detailKey, groups: [] });
      });
    return () => {
      isActive = false;
    };
  }, [assetToken, detailKey, groupToken]);

  const loading = detailState.key !== detailKey;
  useEffect(() => {
    if (loading || !group || !realGroupID || !realAssetID) return;

    setOpenGroups({ [realGroupID]: true });
    setActiveAssetId(realAssetID);

    const target = isReloadRef.current
      ? null
      : document.getElementById(`asset-${realAssetID}`);
    if (target) {
      requestAnimationFrame(() =>
        window.scrollTo({ top: target.offsetTop - 64, behavior: 'auto' })
      );
    }
  }, [group, loading, realAssetID, realGroupID]);

  useEffect(() => {
    if (loading) return;

    const navigation = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;
    if (navigation?.type !== 'reload') return;

    requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'auto' }));
  }, [loading]);

  useEffect(() => {
    if (!group) return;

    const onScroll = () => {
      let currentId = group.assets[0]?.asset_id;
      for (const asset of group.assets) {
        const section = document.getElementById(`asset-${asset.asset_id}`);
        if (section && section.getBoundingClientRect().top <= 120)
          currentId = asset.asset_id;
      }
      if (currentId) setActiveAssetId(currentId);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [group]);

  const toggleGroup = (id: string) => {
    setOpenGroups((previous) => ({ ...previous, [id]: !previous[id] }));
  };

  const scrollToAsset = useCallback(
    (
      event: React.MouseEvent<HTMLAnchorElement>,
      asset: DesignAssetPreview
    ) => {
      event.preventDefault();
      if (!group || !realGroupID) return;
      const target = document.getElementById(`asset-${asset.asset_id}`);
      if (!target) return;

      window.scrollTo({ top: target.offsetTop - 64, behavior: 'smooth' });
      setActiveAssetId(asset.asset_id);
      window.history.replaceState(
        null,
        '',
        buildDesignAssetPath({
          groupID: realGroupID,
          assetID: asset.asset_id,
          groupShortCode: group.group_short_code,
          assetShortCode: asset.asset_short_code,
          search: window.location.search,
        })
      );
    },
    [group, realGroupID]
  );

  if (loading || !group || !realGroupID || !realAssetID) {
    if (loading) {
      return <DetailLoading />;
    }
    return (
      <main className={styles.notFound}>
        <p>板块不存在或已下架。</p>
        <Link href="/design-guidelines">返回品牌规范首页</Link>
      </main>
    );
  }

  return (
    <div className={styles.detailPage} {...pageDtAttrs}>
      <header className={styles.topbar}>
        <button
          aria-controls="design-assets-navigation"
          aria-expanded={mobileMenuOpen}
          aria-label={mobileMenuOpen ? '关闭目录' : '打开目录'}
          className={styles.mobileMenuButton}
          dt-eid={DesignEid.MobileMenuBtn}
          dt-imp-ignore="true"
          onClick={() => setMobileMenuOpen((open) => !open)}
          type="button"
        >
          <DirectoryMenuIcon />
        </button>
        <Link
          className={styles.topbarBrand}
          dt-eid={DesignEid.TopbarBrandLink}
          dt-imp-ignore="true"
          href="/design-guidelines"
        >
          <b>Tencent SSV</b> Brand Guidelines
        </Link>
      </header>

      {mobileMenuOpen ? (
        <button
          aria-label="关闭目录"
          className={styles.mobileMenuMask}
          onClick={() => setMobileMenuOpen(false)}
          type="button"
        />
      ) : null}
      <nav
        aria-label="设计资产目录"
        className={`${styles.sidenav} ${
          mobileMenuOpen ? styles.sidenavOpen : ''
        }`}
        id="design-assets-navigation"
      >
        {groups.map((item) => {
          const expanded = Boolean(openGroups[item.group_id]);
          const isCurrentGroup = item.group_id === realGroupID;
          return (
            <section className={styles.sidenavBlock} key={item.group_id}>
              <button
                className={styles.sidenavTrigger}
                type="button"
                dt-eid={DesignEid.SidenavGroupBtn}
                dt-imp-ignore="true"
                dt-params={dtElementParams({ group_id: item.group_id })}
                onClick={() => toggleGroup(item.group_id)}
                aria-expanded={expanded}
              >
                <span>{item.group_name}</span>
                {expanded ? (
                  <MinusIcon
                    aria-hidden="true"
                    className={styles.sidenavIcon}
                    size="16"
                  />
                ) : (
                  <AddIcon
                    aria-hidden="true"
                    className={styles.sidenavIcon}
                    size="16"
                  />
                )}
              </button>
              {expanded && (
                <ul className={styles.sidenavList}>
                  {item.assets.map((asset) => {
                    const href = buildDesignAssetPath({
                      groupID: item.group_id,
                      assetID: asset.asset_id,
                      groupShortCode: item.group_short_code,
                      assetShortCode: asset.asset_short_code,
                    });
                    const active =
                      isCurrentGroup && activeAssetId === asset.asset_id;
                    return (
                      <li key={asset.asset_id}>
                        <Link
                          className={active ? styles.sidenavActive : undefined}
                          dt-eid={DesignEid.SidenavAssetLink}
                          dt-imp-ignore="true"
                          dt-params={dtElementParams({
                            group_id: item.group_id,
                            asset_id: asset.asset_id,
                          })}
                          href={href}
                          onClick={
                            isCurrentGroup
                              ? (event) => {
                                  scrollToAsset(event, asset);
                                  setMobileMenuOpen(false);
                                }
                              : () => setMobileMenuOpen(false)
                          }
                        >
                          {asset.asset_name}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          );
        })}
      </nav>

      <main className={styles.detailMain}>
        <div className={styles.detailBody}>
          <div className={styles.breadcrumb}>
            <Link
              dt-eid={DesignEid.BreadcrumbHomeLink}
              dt-imp-ignore="true"
              href="/design-guidelines"
            >
              Brand Guidelines
            </Link>
            <span>/</span>
            <span>{group.group_name}</span>
          </div>

          {group.assets.map((asset, index) => (
            <AssetSection
              asset={asset}
              first={index === 0}
              groupID={realGroupID}
              key={asset.asset_id}
              pgid={detailPgid}
            />
          ))}
        </div>
      </main>
    </div>
  );
}

function DetailLoading() {
  return (
    <div className={styles.detailPage} aria-busy="true">
      <header className={styles.topbar}>
        <span aria-hidden="true" className={styles.mobileMenuPlaceholder}>
          <DirectoryMenuIcon />
        </span>
        <Link className={styles.topbarBrand} href="/design-guidelines">
          <b>Tencent SSV</b> Brand Guidelines
        </Link>
      </header>
      <main className={styles.detailLoading} aria-live="polite">
        <span className={styles.loadingIndicator} aria-label="内容加载中" />
      </main>
    </div>
  );
}
